'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';

import { useAppBannerVisibility } from '../useAppBannerVisibility';
import type { AppBannerVariant } from './types';

/** Adjust deeplinks for the test1/test2 variants (2026_08 AppBanner A/B/C test, BAUR only); control keeps `appBanner.appUrl`. */
const variantAppUrls: Partial<Record<AppBannerVariant, string>> = {
  above: 'https://baurde.go.link?adj_t=22oktuf3_22ul7p95',
  below: 'https://baurde.go.link?adj_t=22485822_22a479pk',
};

/** Visibility/cookie/config/tracking logic shared by all AppBanner designs, only the layout and position differ per variant. */
export const useAppBanner = ({
  variant = 'control',
}: {
  variant?: AppBannerVariant;
} = {}) => {
  const {
    appBanner: { appUrl },
    abTesting: { appBannerAbTestId: testId },
  } = useConfig();
  const { isVisible, dismiss } = useAppBannerVisibility();
  const { formatMessage } = useIntl();
  const { setOutcome } = useAbTesting();
  const hasTrackedImpression = useRef(false);

  const variantAppUrl = variantAppUrls[variant] ?? appUrl;

  useEffect(() => {
    if (!isVisible || hasTrackedImpression.current) return;

    if (testId) {
      setOutcome(testId, { IMPRESSION: 1 });
      hasTrackedImpression.current = true;
    }
  }, [isVisible, setOutcome, testId]);

  const handleClick = useCallback(() => {
    if (testId) {
      setOutcome(testId, { COF: 1 });
    }
  }, [setOutcome, testId]);

  const handleClose = useCallback(() => {
    if (testId) {
      setOutcome(testId, { DISMISS: 1 });
    }

    dismiss();
  }, [dismiss, setOutcome, testId]);

  return {
    isVisible,
    appUrl: variantAppUrl,
    variant,
    formatMessage,
    handleClick,
    handleClose,
  };
};
